import './style.scss';
import { supportedCountries } from './supportedCountries';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Tab } from 'semantic-ui-react';
import { useLocalStorage } from 'usehooks-ts';
import { countryToFlag, getLocationInfo } from '../../../../utils/location';
import { usePromise } from '../../../_shared/react-hooks';

import SizeTable, { ITableRow } from './SizeTable';
import _sizeChartData from './sizeChart.json';
import { SizeChart } from './sizeChartTypes';
import {
  generateHeaderRow,
  generateHeaderRowForLengthGuide,
  generateTableDataForBras,
  generateTableDataForLengthGuide,
  generateTableDataForShoesAndSocks,
  generateTableDataForTopsAndBottoms,
} from './SizeChartDataProcessing';
import HeightSelection from './HeightSelection';
import HowToMeasure from './HowToMeasure';
import { SiteSettings } from '../../../../utils/environment';
import { notNil } from '../../../../utils/typescriptUtils';
import AloDropdown, { DropdownItem } from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloDropdown';
import QuickFit from '../QuickFit';
import { isSiteInEnglish } from 'src/scripts/utils/helpers';

const shouldRenderSwimTab = SiteSettings?.enableSwimSizeGuideTab;

// ? [REVISIT] Make it pulling from size chart JSON??? The blocker for now is converting country code to country name.

const sizeChartData: SizeChart = _sizeChartData;
const SizeGuideContent = ({
  gender,
  category,
  subcategory,
  quickFitText,
}: {
  gender: keyof SizeChart;
  category: string | undefined;
  subcategory?: string | undefined;
  quickFitText?: string | null;
}) => {
  const [userPreferedSelections, setUserPreferedSelection] = useLocalStorage<UserPreferedSizeSettings | null>(
    `alo-users-preffered-size-settings`,
    null
  );

  const isWomenGender = gender === 'women';

  const { data: locationInfo, isLoading: isLoadingLocationInfo, error } = usePromise(getLocationInfo);

  useEffect(() => {
    if (
      !userPreferedSelections &&
      !isLoadingLocationInfo &&
      locationInfo &&
      supportedCountries.some((option) => option.value === locationInfo?.country_code.toLowerCase())
    ) {
      const { country_code: countryCode } = locationInfo;
      setUserPreferedSelection({
        currentCountry: countryCode.toLowerCase(),
        systemOfMeasurements: countryCode.toLowerCase() === 'us' ? 'imperial' : 'metric',
      });
    } else if (
      !userPreferedSelections &&
      !isLoadingLocationInfo &&
      locationInfo &&
      supportedCountries.every((option) => option.value !== locationInfo?.country_code.toLowerCase())
    ) {
      setUserPreferedSelection({
        // Outside of US but not supported country
        currentCountry: 'us',
        systemOfMeasurements: 'metric',
      });
    } else if (error) {
      // Default to US and Imperial
      setUserPreferedSelection({
        currentCountry: 'us',
        systemOfMeasurements: 'imperial',
      });
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingLocationInfo]); // Re-running it only when data is loaded

  const toggleMeasurementSystem = (position: string) => {
    // @ts-expect-error
    setUserPreferedSelection({
      ...userPreferedSelections,
      systemOfMeasurements: position === 'right' ? 'imperial' : 'metric',
    });
  };

  const setUsersHeight = (height: string) => {
    // @ts-expect-error
    setUserPreferedSelection({
      ...userPreferedSelections,
      height,
    });
  };

  const tableData = useMemo(() => {
    return {
      tops: {
        headerRows: userPreferedSelections && [
          generateHeaderRow(
            sizeChartData[gender].tops,
            userPreferedSelections.systemOfMeasurements,
            toggleMeasurementSystem
          ),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForTopsAndBottoms(
            sizeChartData[gender].tops,
            sizeChartData[gender].standardChartInInches.tops,
            userPreferedSelections.currentCountry,
            userPreferedSelections.systemOfMeasurements
          ),
      },
      jackets: {
        headerRows: userPreferedSelections && [
          generateHeaderRow(
            sizeChartData[gender].jackets,
            userPreferedSelections.systemOfMeasurements,
            toggleMeasurementSystem
          ),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForTopsAndBottoms(
            sizeChartData[gender].jackets,
            sizeChartData[gender].standardChartInInches.jackets,
            userPreferedSelections.currentCountry,
            userPreferedSelections.systemOfMeasurements
          ),
      },
      bottoms: {
        headerRows: userPreferedSelections && [
          generateHeaderRow(
            sizeChartData[gender].bottoms,
            userPreferedSelections.systemOfMeasurements,
            toggleMeasurementSystem
          ),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForTopsAndBottoms(
            sizeChartData[gender].bottoms,
            sizeChartData[gender].standardChartInInches.bottoms,
            userPreferedSelections.currentCountry,
            userPreferedSelections.systemOfMeasurements
          ),
      },
      shoesAndSocks: {
        headerRows: userPreferedSelections
          ? [
              generateHeaderRow(
                sizeChartData['men'].shoesAndSocks,
                userPreferedSelections.systemOfMeasurements,
                toggleMeasurementSystem,
                false,
                "Alo Shoes - Men's",
                true
              ),
              generateHeaderRow(
                sizeChartData['women'].shoesAndSocks,
                userPreferedSelections.systemOfMeasurements,
                toggleMeasurementSystem,
                false,
                "Alo Shoes - Women's",
                true
              ),
            ]
          : [],
        tableData: userPreferedSelections && generateTableDataForShoesAndSocks(sizeChartData, gender),
      },
      braGuide: {
        headerRows: userPreferedSelections && [
          generateHeaderRow(
            sizeChartData.women.bras,
            userPreferedSelections.systemOfMeasurements,
            toggleMeasurementSystem,
            false,
            'US size'
          ),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForBras(sizeChartData.women.bras, userPreferedSelections.currentCountry),
      },
      onePieces: {
        headerRows: userPreferedSelections && [
          generateHeaderRow(
            sizeChartData.women.onePieces,
            userPreferedSelections.systemOfMeasurements,
            toggleMeasurementSystem
          ),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForTopsAndBottoms(
            sizeChartData.women.onePieces,
            sizeChartData.women.standardChartInInches.onePieces,
            userPreferedSelections.currentCountry,
            userPreferedSelections.systemOfMeasurements
          ),
      },
      lengthGuide: {
        headerRows: userPreferedSelections && [
          generateHeaderRowForLengthGuide(sizeChartData.women.bottoms.lengthGuide, 'Length'),
        ],
        tableData:
          userPreferedSelections &&
          generateTableDataForLengthGuide(
            sizeChartData.women.bottoms.lengthGuide,
            userPreferedSelections.systemOfMeasurements
          ),
      },
      ...(shouldRenderSwimTab && isWomenGender
        ? {
            swimTopFit: {
              headerRows: userPreferedSelections && [
                generateHeaderRow(
                  sizeChartData.women.swim.topFit,
                  userPreferedSelections.systemOfMeasurements,
                  toggleMeasurementSystem
                ),
              ],
              tableData:
                userPreferedSelections &&
                generateTableDataForTopsAndBottoms(
                  sizeChartData.women.swim.topFit,
                  sizeChartData.women.standardChartInInches.swim.topFit,
                  userPreferedSelections.currentCountry,
                  userPreferedSelections.systemOfMeasurements
                ),
            },
            swimBottomFit: {
              headerRows: userPreferedSelections && [
                generateHeaderRow(
                  sizeChartData.women.swim.bottomFit,
                  userPreferedSelections.systemOfMeasurements,
                  toggleMeasurementSystem
                ),
              ],
              tableData:
                userPreferedSelections &&
                generateTableDataForTopsAndBottoms(
                  sizeChartData.women.swim.bottomFit,
                  sizeChartData.women.standardChartInInches.swim.bottomFit,
                  userPreferedSelections.currentCountry,
                  userPreferedSelections.systemOfMeasurements
                ),
            },
            swimOnePieceFit: {
              headerRows: userPreferedSelections && [
                generateHeaderRow(
                  sizeChartData.women.swim.onePieceFit,
                  userPreferedSelections.systemOfMeasurements,
                  toggleMeasurementSystem
                ),
              ],
              tableData:
                userPreferedSelections &&
                generateTableDataForTopsAndBottoms(
                  sizeChartData.women.swim.onePieceFit,
                  sizeChartData.women.standardChartInInches.swim.onePieceFit,
                  userPreferedSelections.currentCountry,
                  userPreferedSelections.systemOfMeasurements
                ),
            },
          }
        : null),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPreferedSelections, gender]);

  // Tabs
  const panes = [
    {
      menuItem: {
        key: isWomenGender ? 'Bras & Tops' : 'Tops',
        content: isWomenGender ? 'Bras & Tops' : 'Tops',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (['Enter', ' '].includes(e.key)) {
            const focusedTab = e.currentTarget;
            (focusedTab as HTMLElement).click();
          }
        },
      },
      render: () => (
        <>
          {quickFitText && activeTabIndex === 0 ? <QuickFit quickFitText={quickFitText} /> : null}
          <Tab.Pane attached={false} className={`size-guide-tab`}>
            {userPreferedSelections && (
              <>
                <SizeTable withToggle headerRows={tableData.tops.headerRows} tableData={tableData.tops.tableData} />
                {isWomenGender && (
                  <>
                    <h6 className="h6 uppercase size-guide-section-title">Bra guide</h6>
                    <SizeTable
                      semanticUIprops={{ className: 'bra-guide-table size-table' }}
                      headerRows={tableData.braGuide.headerRows} // More strict types by passing it not in semanticUIProps
                      tableData={tableData.braGuide.tableData} // More strict types by passing it not in semanticUIProps
                    />
                  </>
                )}
                <HowToMeasure gender={gender} />
              </>
            )}
          </Tab.Pane>
        </>
      ),
    },
    {
      menuItem: {
        key: isWomenGender ? 'Jackets & Sweatshirts' : 'Jackets & Hoodies',
        content: isWomenGender ? 'Jackets & Sweatshirts' : 'Jackets & Hoodies',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (['Enter', ' '].includes(e.key)) {
            const focusedTab = e.currentTarget;
            (focusedTab as HTMLElement).click();
          }
        },
      },
      render: () => (
        <>
          {quickFitText && activeTabIndex === 1 ? <QuickFit quickFitText={quickFitText} /> : null}
          <Tab.Pane attached={false} className={`size-guide-tab`}>
            {userPreferedSelections && (
              <>
                <SizeTable
                  withToggle
                  headerRows={tableData.jackets.headerRows}
                  tableData={tableData.jackets.tableData}
                />
                <HowToMeasure gender={gender} />
              </>
            )}
          </Tab.Pane>
        </>
      ),
    },
    {
      menuItem: {
        key: 'Bottoms',
        content: 'Bottoms',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (['Enter', ' '].includes(e.key)) {
            const focusedTab = e.currentTarget;
            (focusedTab as HTMLElement).click();
          }
        },
      },
      render: () => (
        <>
          {quickFitText && activeTabIndex === 2 ? <QuickFit quickFitText={quickFitText} /> : null}
          <Tab.Pane attached={false}>
            {userPreferedSelections && (
              <>
                <SizeTable
                  withToggle
                  headerRows={tableData.bottoms.headerRows}
                  tableData={tableData.bottoms.tableData}
                />
                {isWomenGender && (
                  <>
                    <h6 className="h6 uppercase size-guide-section-title">Length guide</h6>
                    <SizeTable
                      semanticUIprops={{ className: 'length-guide-table size-table' }}
                      headerRows={tableData.lengthGuide.headerRows}
                      tableData={tableData.lengthGuide.tableData}
                    />
                    <HeightSelection
                      data={sizeChartData.women.bottoms.heightSelection}
                      systemOfMeasurements={userPreferedSelections.systemOfMeasurements}
                      usersHeight={userPreferedSelections.height}
                      setHeight={setUsersHeight}
                    />
                  </>
                )}
                <HowToMeasure gender={gender} />
              </>
            )}
          </Tab.Pane>
        </>
      ),
    },
    {
      menuItem: {
        key: 'Shoes & Socks',
        content: 'Shoes & Socks',
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (['Enter', ' '].includes(e.key)) {
            const focusedTab = e.currentTarget;
            (focusedTab as HTMLElement).click();
          }
        },
      },
      render: () => (
        <Tab.Pane attached={false}>
          {userPreferedSelections && (
            <SizeTable
              semanticUIprops={{ className: 'shoes-socks-table size-table' }}
              headerRows={tableData.shoesAndSocks.headerRows}
              tableData={tableData.shoesAndSocks.tableData}
            />
          )}
        </Tab.Pane>
      ),
    },
    isWomenGender
      ? {
          menuItem: {
            key: 'Bodysuits & Dresses',
            content: 'Bodysuits & Dresses',
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (['Enter', ' '].includes(e.key)) {
                const focusedTab = e.currentTarget;
                (focusedTab as HTMLElement).click();
              }
            },
          },
          render: () => (
            <>
              {quickFitText && activeTabIndex === 4 ? <QuickFit quickFitText={quickFitText} /> : null}
              <Tab.Pane attached={false} className={`size-guide-tab`}>
                {userPreferedSelections && (
                  <>
                    <SizeTable
                      withToggle
                      headerRows={tableData.onePieces.headerRows}
                      tableData={tableData.onePieces.tableData}
                    />
                    {isWomenGender && (
                      <>
                        <h6 className="h6 uppercase size-guide-section-title">Bra guide</h6>
                        <SizeTable
                          semanticUIprops={{ className: 'bra-guide-table size-table' }}
                          headerRows={tableData.braGuide.headerRows} // More strict types by passing it not in semanticUIProps
                          tableData={tableData.braGuide.tableData} // More strict types by passing it not in semanticUIProps
                        />
                      </>
                    )}
                    <HowToMeasure gender={gender} />
                  </>
                )}
              </Tab.Pane>
            </>
          ),
        }
      : null,
    shouldRenderSwimTab && isWomenGender
      ? {
          menuItem: {
            key: 'Swim',
            content: 'Swim',
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (['Enter', ' '].includes(e.key)) {
                const focusedTab = e.currentTarget;
                (focusedTab as HTMLElement).click();
              }
            },
          },
          render: () => {
            //  w/o xxl
            // [REVISIT] quick fix with a bang(!). How appropriate to have it here
            const topFit = omitXxl(tableData.swimTopFit!);
            const bottomFit = omitXxl(tableData.swimBottomFit!);
            const onePieceFit = omitXxl(tableData.swimOnePieceFit!);

            return (
              <Tab.Pane attached={false} className={`size-guide-tab swim`}>
                {userPreferedSelections && (
                  <>
                    <h6 style={{ marginTop: 16 }} className="h6 uppercase size-guide-section-title">
                      Top fit
                    </h6>
                    <SizeTable withToggle headerRows={topFit.headerRows} tableData={topFit.tableData} />
                    <h6 className="h6 uppercase size-guide-section-title">Bottom fit</h6>
                    <SizeTable withToggle headerRows={bottomFit.headerRows} tableData={bottomFit.tableData} />
                    <h6 className="h6 uppercase size-guide-section-title">One Piece fit</h6>
                    <SizeTable withToggle headerRows={onePieceFit.headerRows} tableData={onePieceFit.tableData} />
                    <HowToMeasure gender={gender} />
                  </>
                )}
              </Tab.Pane>
            );
          },
        }
      : null,
  ].filter(notNil);

  const getActiveTabIndex = useCallback((category: string, subcategory: string): number | undefined => {
    const categoryTaxonomy = {
      tops: ['bras', 'tanks', 'tops'],
      jackets: ['outerwear'],
      bottoms: ['shorts', 'capris', 'leggings', 'bottoms', 'undergarments'],
      shoesAndSocks: ['shoes', 'socks'],
      onePieces: ['one piece'],
      swim: ['swimwear'],
    };

    for (const [key, value] of Object.entries(categoryTaxonomy)) {
      if (value.includes(category) || value.includes(subcategory)) {
        const keys = Object.keys(categoryTaxonomy);
        const index = keys.indexOf(key);
        return index > -1 ? index : 0;
      }
    }
  }, []);

  const activeTabIndex = useMemo(
    () => (category || subcategory ? getActiveTabIndex(category || '', subcategory || '') : 0),
    [category, subcategory, getActiveTabIndex]
  );
  const dropdownItems = supportedCountries.map(({ key, text, flag }) => ({
    key,
    text,
    flag,
  }));

  const dropdownDefaultValue = supportedCountries.find(
    (country) => country.key === userPreferedSelections?.currentCountry
  );

  const dropdownValue = {
    key: dropdownDefaultValue?.key || '',
    text: dropdownDefaultValue?.text || '',
    flag: dropdownDefaultValue?.flag || '',
  };

  const onDropdownChange = (selectedCountry: DropdownItem) => {
    setUserPreferedSelection({
      ...userPreferedSelections,
      systemOfMeasurements: selectedCountry?.key === 'us' ? 'imperial' : 'metric',
      currentCountry: selectedCountry?.key as string,
    });
  };

  const renderOption = (option: DropdownItem) => (
    <>
      {option?.flag ? <span>{countryToFlag(option.flag)}</span> : null}
      <span style={{ marginLeft: '1rem' }}>{option.text}</span>
    </>
  );

  const selectedCountry =
    userPreferedSelections?.currentCountry === 'uk' ? 'gb' : userPreferedSelections?.currentCountry;
  const flagElement = userPreferedSelections?.currentCountry ? (
    <span>{countryToFlag(selectedCountry || '')}</span>
  ) : null;

  return userPreferedSelections ? (
    <div className="size-guide-content">
      <div className="size-guide-dropdown-wrapper">
        <AloDropdown
          searchable
          inputReadOnly={!isSiteInEnglish()}
          searchableDropdownPrependIcon={flagElement}
          dropdownItems={dropdownItems}
          onChange={onDropdownChange}
          placeholder="Select Country"
          value={dropdownValue}
          wrapperClass="size-guide-dropdown"
          MUIAutocompleteProps={{
            disableClearable: true,
            renderOption,
          }}
        />
      </div>
      <Tab
        className="size-guide-tabs"
        defaultActiveIndex={activeTabIndex || 0}
        menu={{ secondary: true, pointing: true }}
        panes={panes}
      />
    </div>
  ) : (
    <div className="loading small-p mx-auto text-center">Loading...</div>
  );
};

export default SizeGuideContent;

// -- temporary merch request
const omitXxl = (hash: { headerRows: null | ((string | JSX.Element)[] | null)[]; tableData: ITableRow[] | null }) => {
  // perfect usecase for immer
  const copy = { ...hash };
  // -- these types are janky enough we have to slice the last entry off & assume it's Xxl :(
  copy.headerRows = copy.headerRows?.slice(0, -1) ?? null;
  copy.tableData =
    copy.tableData?.map((row) => {
      const rowCopy = { ...row };
      delete rowCopy['xxl'];
      return rowCopy;
    }) ?? null;
  return copy;
};

export interface UserPreferedSizeSettings {
  currentCountry: string;
  systemOfMeasurements: 'imperial' | 'metric';
  height?: string;
}
